import { useCallback, useState } from 'react'
import { GiTurnstile } from 'react-icons/gi'

import { Date as DateGateway, Alert } from '@core/gateways'
import { RecepcaoService } from '@core/services'
import { ConsultaTransitoItem } from '@core/types/services'
import { TablePage } from '@presentation/components/templates'
import { ProfileDetailsModal } from '@presentation/pages/recepcao/TransitoCatracaPage/components'
import { parseToRequest } from '@presentation/utils'
import { FetchProps } from '@presentation/types/hooks'
import { PageProps } from '@presentation/types/components/templates'
import {
  Filter,
  FilterProps,
  TableColumn,
  TableProps
} from '@presentation/types/components/organisms'

const formatDateTime = (value: string) => {
  if (!value) return ''

  return DateGateway.format({
    date: value,
    pattern: 'dd/LL/yyyy HH:mm'
  })
}

const getResponseItems = (response: any): ConsultaTransitoItem[] => {
  const data = response?.data ?? response
  const nestedData = data?.data ?? data
  const items = nestedData?.items ?? data?.items ?? []

  return Array.isArray(items) ? items : []
}

const getPessoaId = (item: ConsultaTransitoItem) => {
  const pessoaId = item.pessoaId ?? item.pessoa_id

  if (pessoaId === null || pessoaId === undefined || pessoaId === '') {
    return undefined
  }

  return String(pessoaId)
}

const getResponseTotal = (response: any, items: ConsultaTransitoItem[]) => {
  const data = response?.data ?? response
  const nestedData = data?.data ?? data

  return (
    nestedData?.pagination?.total ?? data?.pagination?.total ?? items.length
  )
}

export const ConsultaTransitoPage: React.FC = () => {
  const [data, setData] = useState<ConsultaTransitoItem[]>([])
  const [isFetching, setIsFetching] = useState(true)
  const [totalItems, setTotalItems] = useState(0)

  const mapParamsToRequest = (params: any) => {
    const { sortBy, sortDir, perPage, dataInicio, dataFim, ...rest } = params

    const optionalParams = Object.fromEntries(
      Object.entries({
        ...rest,
        data_inicio: dataInicio,
        data_fim: dataFim
      }).filter(([, value]) => value !== undefined && value !== '')
    )

    const mergedParams = {
      per_page: perPage ?? 20,
      sort_by: sortBy,
      sort_dir: sortDir,
      ...(params.page !== undefined ? { page: params.page } : {}),
      ...optionalParams
    }

    return parseToRequest(mergedParams)
  }

  const fetchData = useCallback(async (input?: FetchProps) => {
    setIsFetching(true)

    const params = Object.assign(
      {},
      {
        perPage: 20,
        sortBy: 'dia_hora',
        sortDir: 'desc'
      },
      input?.options,
      input?.queries
    )

    try {
      const response = await RecepcaoService.obterConsultaTransitos({
        params: mapParamsToRequest(params)
      })
      const items = getResponseItems(response)

      setData(items)
      setTotalItems(getResponseTotal(response, items))
    } catch (error) {
      await Alert.call({
        type: 'error',
        title: 'Falha ao Carregar Trânsitos',
        description:
          'Tente novamente mais tarde. Caso o problema persista, contate alguém do suporte técnico.'
      })
    } finally {
      setIsFetching(false)
    }
  }, [])

  const pageConfigs: PageProps = {
    title: 'Consulta de Trânsito',
    icon: <GiTurnstile />,
    description: 'Página de consulta de trânsito de catraca de pessoas'
  }

  const filters: Filter[] = [
    {
      type: {
        value: 'nome',
        label: 'Nome'
      },
      description: {
        type: 'input',
        configs: {
          type: 'text'
        }
      }
    },
    {
      type: {
        value: 'identificacao',
        label: 'Identificação'
      },
      description: {
        type: 'input',
        configs: {
          type: 'text'
        }
      }
    },
    {
      type: {
        value: 'dataInicio',
        label: 'Data início'
      },
      description: {
        type: 'input',
        configs: {
          type: 'date'
        }
      }
    },
    {
      type: {
        value: 'dataFim',
        label: 'Data fim'
      },
      description: {
        type: 'input',
        configs: {
          type: 'date'
        }
      }
    }
  ]

  const filterConfigs: FilterProps = {
    filters,
    fetchData,
    hasStatus: false
  }

  const columns: Array<TableColumn<ConsultaTransitoItem>> = [
    {
      name: 'Dia e hora',
      selector: item => formatDateTime(item.dia_hora),
      center: true,
      grow: 1.2,
      sortable: true,
      sortField: 'dia_hora'
    },
    {
      name: 'Nome',
      selector: item => item.nome ?? '',
      center: true,
      grow: 2,
      sortable: true,
      sortField: 'nome'
    },
    {
      name: 'Identificação',
      selector: item => item.identificacao ?? '',
      center: true,
      sortable: true,
      sortField: 'identificacao'
    },
    {
      name: 'Tipo da Identificação',
      selector: item => item.tipo_identificacao ?? '',
      center: true,
      grow: 1.3,
      sortable: true,
      sortField: 'tipo_identificacao'
    },
    {
      name: 'Tipo de Movimentação',
      selector: item => item.tipo_movimentacao ?? '',
      center: true,
      grow: 1.3,
      sortable: true,
      sortField: 'tipo_movimentacao'
    },
    {
      name: 'Ações',
      cell: item => (
        <ProfileDetailsModal
          id={getPessoaId(item)}
          nome={item.nome}
          email={item.email ?? undefined}
          identificacao={item.identificacao}
          tipo={item.tipo_identificacao}
        />
      ),
      center: true,
      width: '90px'
    }
  ]

  const tableConfigs: TableProps = {
    columns,
    data,
    apiConfigs: {
      fetchData,
      isFetching,
      totalItems
    }
  }

  return (
    <TablePage
      pageConfigs={pageConfigs}
      filterConfigs={filterConfigs}
      tableConfigs={tableConfigs}
    />
  )
}

export default ConsultaTransitoPage
